import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, GripVertical, Upload, User, Briefcase } from 'lucide-react';

const TeamManagement = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    image: '',
    order: 0,
    is_job_listing: false,
    linked_job_id: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch team:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch jobs:', error);
      }
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchMembers();
    fetchJobs();
  }, [fetchMembers, fetchJobs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingMember 
      ? `${API_URL}/api/admin/team/${editingMember.id}`
      : `${API_URL}/api/admin/team`;
    
    const method = editingMember ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          order: formData.order || members.length
        })
      });

      if (response.ok) {
        fetchMembers();
        closeModal();
      }
    } catch (error) {
      console.error('Failed to save member:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Mitarbeiter wirklich löschen?')) return;

    try {
      await fetch(`${API_URL}/api/admin/team/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchMembers();
    } catch (error) {
      console.error('Failed to delete member:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: `${API_URL}${data.url}` }));
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      image: member.image || '',
      order: member.order,
      is_job_listing: member.is_job_listing,
      linked_job_id: member.linked_job_id || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setFormData({ name: '', role: '', image: '', order: 0, is_job_listing: false, linked_job_id: '' });
  };

  const moveItem = async (index, direction) => {
    const newMembers = [...members];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newMembers.length) return;

    [newMembers[index], newMembers[targetIndex]] = [newMembers[targetIndex], newMembers[index]];
    
    const orderUpdate = newMembers.map((m, i) => ({ id: m.id, order: i }));
    
    try {
      await fetch(`${API_URL}/api/admin/team/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderUpdate)
      });
      fetchMembers();
    } catch (error) {
      console.error('Failed to reorder:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>Mitarbeiter</h1>
          <p className="text-gray-500">Team verwalten und bearbeiten</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          style={{ backgroundColor: '#1B4D3E' }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Hinzufügen
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {members.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Noch keine Mitarbeiter vorhanden
          </div>
        ) : (
          <div className="divide-y">
            {members.map((member, index) => (
              <div key={member.id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 rotate-180" />
                  </button>
                  <button
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === members.length - 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                </div>

                <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{member.name}</h3>
                  <p className="text-sm text-gray-500">{member.role}</p>
                  {member.is_job_listing && (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                      Stellenanzeige
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(member)}
                    className="p-2 hover:bg-gray-200 rounded-lg"
                  >
                    <Pencil className="w-4 h-4" style={{ color: '#1B4D3E' }} />
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="p-2 hover:bg-red-100 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#1B4D3E' }}>
              {editingMember ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bezeichnung</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bild</label>
                <div className="flex items-center gap-4">
                  {formData.image && (
                    <img src={formData.image} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
                  )}
                  <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <Upload className="w-4 h-4" />
                    Bild hochladen
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg mt-2"
                  placeholder="Oder Bild-URL eingeben"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_job_listing"
                  checked={formData.is_job_listing}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    is_job_listing: e.target.checked,
                    linked_job_id: e.target.checked ? prev.linked_job_id : ''
                  }))}
                  className="rounded"
                />
                <label htmlFor="is_job_listing" className="text-sm">
                  Als Stellenanzeige markieren
                </label>
              </div>

              {/* Job Selection - only show when is_job_listing is checked */}
              {formData.is_job_listing && jobs.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Briefcase className="w-4 h-4 inline mr-1" />
                    Mit Stellenanzeige verknüpfen
                  </label>
                  <select
                    value={formData.linked_job_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, linked_job_id: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="">-- Keine Verknüpfung --</option>
                    {jobs.filter(j => j.is_active).map(job => (
                      <option key={job.id} value={job.id}>
                        {job.title} ({job.employment_type || 'Keine Angabe'})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Wählen Sie eine aktive Stellenanzeige, auf die verlinkt werden soll.
                  </p>
                </div>
              )}

              {formData.is_job_listing && jobs.length === 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  Noch keine Stellenanzeigen vorhanden. Erstellen Sie zuerst eine Stellenanzeige unter "Stellenanzeigen".
                </p>
              )}

              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={closeModal} className="flex-1">
                  Abbrechen
                </Button>
                <Button type="submit" className="flex-1" style={{ backgroundColor: '#1B4D3E' }}>
                  Speichern
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
